// src/dashboard/pages/teacher/Palmares/hooks/usePalmaresDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllPalmares,
  addPalmares,
  updatePalmares,
  deletePalmares,
  togglePalmaresIsActive,
} from "../../../../../services/palmaresServices";

import { getSettings } from "../../../../../services/settingsServices";
import { getAllAdherentsList } from "../../../../../services/adherentServices";
import { getAllEvents } from "../../../../../services/eventServices";

import {
  buildPalmaresPayload,
  calculatePalmaresStats,
  filterPalmares,
  getActiveSeasonFromSettings,
  getPalmaresMemberOptions,
  getSeasonOptions,
  sortEvents,
  sortMembers,
  sortPalmares,
} from "../utils/palmaresDashboardUtils";

const defaultFilters = {
  query: "",
  season: "",
  visibility: "",
  sort: "date_desc",
};

export function usePalmaresDashboard() {
  const [palmares, setPalmares] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [events, setEvents] = useState([]);

  const [activeSeason, setActiveSeason] = useState("");
  const [filters, setFilters] = useState(defaultFilters);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [currentPalmares, setCurrentPalmares] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const [
        palmaresResponse,
        settingsResponse,
        adherentsResponse,
        eventsResponse,
      ] = await Promise.all([
        getAllPalmares(),
        getSettings(),
        getAllAdherentsList(),
        getAllEvents(),
      ]);

      const palmaresRows = Array.isArray(palmaresResponse)
        ? palmaresResponse
        : [];

      const adherentsRows = Array.isArray(adherentsResponse)
        ? adherentsResponse
        : [];

      const eventsRows = Array.isArray(eventsResponse) ? eventsResponse : [];

      const currentSeason = getActiveSeasonFromSettings(settingsResponse);

      setPalmares(sortPalmares(palmaresRows, "date_desc"));
      setAdherents(sortMembers(adherentsRows));
      setEvents(sortEvents(eventsRows));
      setActiveSeason(currentSeason);

      setFilters((prev) => ({
        ...prev,
        season: prev.season || currentSeason || "",
      }));
    } catch (error) {
      console.error("Erreur chargement palmarès :", error);
      toast.error("Erreur lors du chargement des palmarès.");
      setPalmares([]);
      setAdherents([]);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const seasonOptions = useMemo(() => {
    return getSeasonOptions(palmares, activeSeason);
  }, [palmares, activeSeason]);

  const palmaresMemberOptions = useMemo(() => {
  return getPalmaresMemberOptions(palmares);
}, [palmares]);

  const filteredPalmares = useMemo(() => {
    return filterPalmares(palmares, filters);
  }, [palmares, filters]);

  const stats = useMemo(() => {
    return calculatePalmaresStats(palmares, filteredPalmares, activeSeason);
  }, [palmares, filteredPalmares, activeSeason]);

  const openAddDrawer = () => {
    setAddDrawerOpen(true);
  };

  const closeAddDrawer = () => {
    if (saving) return;
    setAddDrawerOpen(false);
  };

  const openEditDrawer = (item) => {
    setCurrentPalmares(item);
    setEditDrawerOpen(true);
  };

  const closeEditDrawer = () => {
    if (saving) return;
    setCurrentPalmares(null);
    setEditDrawerOpen(false);
  };

  const handleCreatePalmares = async (formData) => {
    try {
      setSaving(true);

      const payload = buildPalmaresPayload(formData, {
        includeAdherent: true,
      });

      await addPalmares(payload);

      toast.success("Palmarès ajouté avec succès.");
      setAddDrawerOpen(false);

      await fetchDashboardData();
    } catch (error) {
      console.error("Erreur création palmarès :", error);
      toast.error("Erreur lors de la création du palmarès.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePalmares = async (id, formData) => {
    if (!id) {
      toast.error("Palmarès introuvable.");
      return;
    }

    try {
      setSaving(true);

      const payload = buildPalmaresPayload(formData, {
        includeAdherent: false,
      });

      await updatePalmares(id, payload);

      toast.success("Palmarès mis à jour.");
      setEditDrawerOpen(false);
      setCurrentPalmares(null);

      await fetchDashboardData();
    } catch (error) {
      console.error("Erreur mise à jour palmarès :", error);
      toast.error("Erreur lors de la mise à jour du palmarès.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePalmares = async (itemOrId) => {
    const id = typeof itemOrId === "object" ? itemOrId?.id : itemOrId;

    if (!id) {
      toast.error("Palmarès introuvable.");
      return;
    }

    if (!window.confirm("Supprimer ce palmarès ?")) {
      return;
    }

    try {
      setDeletingId(id);

      await deletePalmares(id);

      toast.success("Palmarès supprimé.");

      setPalmares((prev) => prev.filter((item) => item.id !== id));

      if (currentPalmares?.id === id) {
        setCurrentPalmares(null);
        setEditDrawerOpen(false);
      }
    } catch (error) {
      console.error("Erreur suppression palmarès :", error);
      toast.error("Erreur lors de la suppression du palmarès.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleIsActive = async (id) => {
    if (!id) {
      toast.error("Palmarès introuvable.");
      return;
    }

    try {
      setTogglingId(id);

      const result = await togglePalmaresIsActive(id);

      const nextValue =
        typeof result?.is_active === "boolean" ? result.is_active : undefined;

      setPalmares((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                is_active:
                  nextValue === undefined ? !item.is_active : nextValue,
              }
            : item,
        ),
      );

      toast.success(result?.message || "Visibilité mise à jour.");
    } catch (error) {
      console.error("Erreur changement visibilité palmarès :", error);
      toast.error("Erreur lors du changement de visibilité.");
    } finally {
      setTogglingId(null);
    }
  };

  const refreshPalmares = useCallback(async () => {
    await fetchDashboardData();
  }, [fetchDashboardData]);

  return {
  palmares,
  filteredPalmares,
  adherents,
  events,
  palmaresMemberOptions,
  seasonOptions,
  activeSeason,
  stats,

  filters,
  setFilters,

  loading,
  saving,
  deletingId,
  togglingId,

  addDrawerOpen,
  editDrawerOpen,
  currentPalmares,

  openAddDrawer,
  closeAddDrawer,
  openEditDrawer,
  closeEditDrawer,

  handleCreatePalmares,
  handleUpdatePalmares,
  handleDeletePalmares,
  handleToggleIsActive,
  refreshPalmares,
};
}
